/**
 * useGlobalLoaderVisible — pilotage partagé des variantes du loader global.
 *
 * @remarks
 * Séparé des composants (`GlobalLoader`, `GlobalLoaderMindMap`) pour que le
 * fast refresh reste fonctionnel (un fichier de composants ne doit exporter
 * que des composants).
 */

import { useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';

const MIN_VISIBLE_MS = 400;

/**
 * Hook de pilotage partagé par les variantes visuelles du loader global :
 * `true` tant qu'il faut afficher un voile (premier chargement en cours, ou
 * durée minimale pas encore écoulée).
 *
 * Seul le premier chargement d'une donnée voile l'écran : rien n'y est encore
 * affiché. Les mutations n'y comptent plus (SAAS-1269) : chacune montre son
 * retour sur place (`Button loading`, statut d'enregistrement…). Avant, toute
 * mutation hors `/admin` voilait le panneau central — l'auto-sauvegarde du CV
 * le faisait à chaque pause de frappe, à côté de son propre indicateur.
 */
export function useGlobalLoaderVisible(): boolean {
  // `status === 'pending'` = la query n'a encore jamais reçu de donnée.
  // Un refetch d'une query `success` (donnée déjà affichée) est ignoré.
  const fetching = useIsFetching({ predicate: (query) => query.state.status === 'pending' });
  const active = fetching > 0;
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      shownAtRef.current ??= Date.now();
      setVisible(true);
      return;
    }
    if (shownAtRef.current === null) return;

    // Activité terminée : garder le voile jusqu'à la durée minimale.
    const elapsed = Date.now() - shownAtRef.current;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const timer = setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
    }, remaining);
    return () => {
      clearTimeout(timer);
    };
  }, [active]);

  // `active` est lu de façon synchrone : le voile (et le masquage des loaders
  // locaux) est vrai dès le premier rendu, sans attendre l'effet ci-dessus.
  return active || visible;
}
